import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MapPin, Building2, Store, Home, Briefcase, Phone, Send, Share2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '@/contexts/CurrencyContext';
import { formatPriceRange } from '@/lib/formatPrice';
import { optimizedImageUrl } from '@/lib/imageOptimizer';
import { cn } from '@/lib/utils';
import { CallContactDialog } from '@/components/property/CallContactDialog';
import { ProjectReserveDialog } from '@/components/project/ProjectReserveDialog';

interface UnitType {
  id: string;
  name: string;
  area_min?: number | null;
  area_max?: number | null;
  price_min?: number | null;
  price_max?: number | null;
  currency?: string | null;
  sort_order?: number | null;
}

interface ProjectListingCardProps {
  project: any;
  layout?: 'list' | 'grid';
}

function pickIcon(name: string) {
  const n = name.toLowerCase();
  if (n.includes('shop') || n.includes('retail')) return Store;
  if (n.includes('office')) return Briefcase;
  if (n.includes('villa') || n.includes('penthouse') || n.includes('house')) return Home;
  return Building2;
}

function unitAreaLabel(u: UnitType) {
  if (u.area_min == null && u.area_max == null) return '—';
  if (u.area_min != null && u.area_max != null && u.area_min !== u.area_max)
    return `${u.area_min} – ${u.area_max} m²`;
  return `${u.area_min ?? u.area_max} m²`;
}

export function ProjectListingCard({ project, layout = 'list' }: ProjectListingCardProps) {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = i18n.language || 'en';
  const { displayCurrency, rate } = useCurrency();
  const [callOpen, setCallOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);

  const name = useMemo(() => {
    const v = project.name;
    if (typeof v === 'object') return v?.[lang] || v?.en || '';
    return v || '';
  }, [project.name, lang]);

  const description = useMemo(() => {
    const v = project.description;
    if (typeof v === 'object') return v?.[lang] || v?.en || '';
    return v || '';
  }, [project.description, lang]);

  const img = optimizedImageUrl(
    project.project_images?.find((i: any) => i.is_primary)?.url
      || project.project_images?.[0]?.url
      || 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600&h=400&fit=crop',
    { width: 480, height: 360, quality: 78 },
  );

  const isSoldOut = (project.total_units || 0) > 0 && (project.available_units || 0) === 0;
  const slug = project.slug || project.id;
  const goDetail = () => navigate(`/projects/${slug}`);

  const dev = project.developer_profiles;
  const agency = project.agencies; // joined as marketing_agency_id
  const marketedBy = agency?.name || dev?.company_name;
  const marketedLogo = agency?.logo_url || dev?.logo_url;
  const phone = dev?.phone || agency?.phone;
  const wa = phone?.replace(/[^\d]/g, '');

  const priceLabel = (project.price_min || project.price_max)
    ? formatPriceRange(project.price_min, project.price_max, project.currency || 'ETB', displayCurrency, rate)
    : 'Contact for price';

  const units: UnitType[] = (project.project_unit_types || [])
    .slice()
    .sort((a: UnitType, b: UnitType) => (a.sort_order || 0) - (b.sort_order || 0));
  const visibleUnits = units.slice(0, 3);
  const moreUnits = units.length - visibleUnits.length;

  const onShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    const url = `${window.location.origin}/projects/${slug}`;
    if (navigator.share) navigator.share({ title: name, url }).catch(() => {});
    else navigator.clipboard?.writeText(url);
  };

  const SoldOutRibbon = () => (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 -rotate-[20deg] w-[140%] py-1.5 bg-destructive/85 text-destructive-foreground text-center font-extrabold tracking-[0.3em] text-sm shadow-md">
        SOLD OUT
      </div>
    </div>
  );

  const MarketedBy = () => marketedBy ? (
    <div className="inline-flex items-center gap-1.5 border rounded-md px-2 py-1 bg-background shrink-0">
      <span className="text-[9px] uppercase text-muted-foreground tracking-wide">Marketed By</span>
      {marketedLogo
        ? <img src={marketedLogo} alt={marketedBy} className="h-3.5 max-w-[80px] object-contain" />
        : <span className="text-[10px] font-semibold text-primary truncate max-w-[100px]">{marketedBy}</span>}
    </div>
  ) : null;

  const ActionButtons = ({ size = 'sm' }: { size?: 'sm' | 'xs' }) => {
    const h = size === 'xs' ? 'h-8 text-xs' : 'h-9 text-xs';
    return (
      <div className="flex items-center gap-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
        <Button variant="outline" className={cn(h, 'gap-1.5')} onClick={() => setInfoOpen(true)}>
          <Send className="h-3.5 w-3.5" /> Get More Info
        </Button>
        {phone && (
          <Button className={cn(h, 'gap-1.5')} onClick={() => setCallOpen(true)}>
            <Phone className="h-3.5 w-3.5" /> Call
          </Button>
        )}
        {wa && (
          <a
            href={`https://wa.me/${wa}?text=${encodeURIComponent(`Hi, I'm interested in ${name} (${project.reference_code || ''})`)}`}
            target="_blank" rel="noopener noreferrer"
            className={cn(h, 'inline-flex items-center justify-center px-3 rounded-md bg-whatsapp text-whatsapp-foreground hover:opacity-90')}
            aria-label="WhatsApp"
            onClick={(e) => e.stopPropagation()}
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor"><path d="M20.52 3.48A11.94 11.94 0 0 0 12.06 0C5.5 0 .16 5.34.16 11.9c0 2.1.55 4.15 1.6 5.96L0 24l6.32-1.66a11.9 11.9 0 0 0 5.74 1.46h.01c6.56 0 11.9-5.34 11.9-11.9 0-3.18-1.24-6.17-3.45-8.42zM12.07 21.3h-.01a9.86 9.86 0 0 1-5.03-1.38l-.36-.21-3.75.98 1-3.65-.24-.38a9.85 9.85 0 1 1 18.32-5.16c0 5.45-4.43 9.8-9.93 9.8z"/></svg>
          </a>
        )}
      </div>
    );
  };

  const Dialogs = () => (
    <>
      <CallContactDialog
        open={callOpen}
        onOpenChange={setCallOpen}
        agentName={dev?.company_name || agency?.name || 'Project Team'}
        agencyName={agency?.name}
        mobileNumber={phone}
        referenceId={project.reference_code || project.id}
        listingId={project.id}
        listingType="project"
      />
      <ProjectReserveDialog
        open={infoOpen}
        onOpenChange={setInfoOpen}
        projectId={project.id}
        projectName={name}
        referenceCode={project.reference_code}
        assignedAgentId={project.assigned_agent_id}
      />
    </>
  );

  // ============ GRID (compact) ============
  if (layout === 'grid') {
    return (
      <div onClick={goDetail} className="rounded-xl border bg-card overflow-hidden hover:shadow-md transition-shadow cursor-pointer flex flex-col">
        <div className="relative aspect-[4/3]">
          <img src={img} alt={name} loading="lazy" className="w-full h-full object-cover" />
          <button onClick={onShare} aria-label="Share"
            className="absolute top-2 right-2 h-7 w-7 rounded-full bg-background/90 border flex items-center justify-center hover:bg-background">
            <Share2 className="h-3.5 w-3.5" />
          </button>
          {isSoldOut && <SoldOutRibbon />}
        </div>
        <div className="p-3 flex-1 flex flex-col gap-1.5">
          <div className="font-bold text-sm leading-tight">{priceLabel}</div>
          <div className="font-semibold text-sm line-clamp-1">{name}</div>
          <MarketedBy />
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <MapPin className="h-3 w-3 text-primary" />
            <span className="truncate">{project.sub_city ? `${project.sub_city}, ` : ''}{project.city}</span>
          </div>
          {visibleUnits.length > 0 && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Building2 className="h-3 w-3" />
              <span className="truncate">{visibleUnits.map(u => u.name).join(', ')}{moreUnits > 0 ? `, +${moreUnits}` : ''}</span>
            </div>
          )}
          <div className="mt-auto pt-2"><ActionButtons size="xs" /></div>
        </div>
        <Dialogs />
      </div>
    );
  }

  // ============ LIST (horizontal) ============
  return (
    <div onClick={goDetail} className="rounded-xl border bg-card overflow-hidden hover:shadow-md transition-shadow cursor-pointer">
      <div className="grid grid-cols-1 sm:grid-cols-[280px_1fr] md:grid-cols-[320px_1fr]">
        <div className="relative aspect-[4/3] sm:aspect-auto sm:h-full min-h-[220px]">
          <img src={img} alt={name} loading="lazy" className="w-full h-full object-cover" />
          <button onClick={onShare} aria-label="Share"
            className="absolute top-2 right-2 h-8 w-8 rounded-full bg-background/90 border flex items-center justify-center hover:bg-background">
            <Share2 className="h-4 w-4" />
          </button>
          {isSoldOut && <SoldOutRibbon />}
        </div>

        <div className="p-4 flex flex-col gap-2">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="font-bold text-base md:text-lg leading-tight truncate">{name}</h3>
              <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                <MapPin className="h-3.5 w-3.5 text-primary" />
                <span className="truncate">{project.sub_city ? `${project.sub_city}, ` : ''}{project.city}</span>
              </div>
            </div>
            <MarketedBy />
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-base text-foreground">{priceLabel}</span>
            {project.status && (
              <Badge variant="outline" className="text-[10px] capitalize border-primary/40 text-primary">
                {String(project.status).replace(/_/g, ' ')}
              </Badge>
            )}
          </div>

          {description && (
            <p className="text-xs text-muted-foreground line-clamp-2">{description}</p>
          )}

          {visibleUnits.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-1">
              {visibleUnits.map((u) => {
                const Icon = pickIcon(u.name);
                return (
                  <div key={u.id} className="border rounded-lg px-3 py-2 flex items-center gap-3 min-w-[160px] bg-background">
                    <div className="min-w-0">
                      <div className="text-xs font-semibold truncate">{u.name}</div>
                      <div className="text-[10px] text-muted-foreground">{unitAreaLabel(u)}</div>
                      {u.price_min != null && (
                        <div className="text-[10px] text-muted-foreground">
                          From {formatPriceRange(u.price_min, u.price_min, u.currency || project.currency || 'ETB', displayCurrency, rate)}
                        </div>
                      )}
                    </div>
                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <Icon className="h-4 w-4 text-primary" />
                    </div>
                  </div>
                );
              })}
              {moreUnits > 0 && (
                <div className="border rounded-lg px-3 py-2 flex items-center text-xs text-primary font-semibold bg-background">
                  +{moreUnits} more
                </div>
              )}
            </div>
          )}

          <div className="mt-2"><ActionButtons /></div>
        </div>
      </div>
      <Dialogs />
    </div>
  );
}
